import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/auth/Login.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('../views/auth/ChangePassword.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/',
    component: () => import('../components/layout/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/dashboard/Dashboard.vue')
      },
      {
        path: 'payin/pending',
        name: 'PendingPayin',
        component: () => import('../views/payin/PendingPayin.vue')
      },
      {
        path: 'payin/all',
        name: 'AllPayin',
        component: () => import('../views/payin/AllPayin.vue')
      },
      {
        path: 'payout/pending',
        name: 'PendingPayout',
        component: () => import('../views/payout/PendingPayout.vue')
      },
      {
        path: 'payout/all',
        name: 'AllPayout',
        component: () => import('../views/payout/AllPayout.vue')
      },
      {
        path: 'merchants',
        name: 'Merchants',
        component: () => import('../views/merchants/Merchants.vue')
      },
      {
        path: 'merchants/:id',
        name: 'MerchantDetails',
        component: () => import('../views/merchants/MerchantDetails.vue')
      },
      {
        path: 'settlement/topup-requests',
        name: 'TopupRequests',
        component: () => import('../views/settlement/TopupRequests.vue')
      },
      {
        path: 'settlement/withdrawal-requests',
        name: 'WithdrawalRequests',
        component: () => import('../views/settlement/WithdrawalRequests.vue')
      },
      {
        path: 'providers',
        name: 'ProviderIntegrations',
        component: () => import('../views/providers/ProviderIntegrations.vue')
      },
      {
        path: 'api-providers',
        redirect: '/providers'
      },
      {
        path: 'api-providers/:id/statement',
        name: 'ProviderStatement',
        component: () => import('../views/providers/ProviderStatement.vue')
      },
      {
        path: 'bank-providers',
        redirect: '/providers'
      },
      {
        path: 'financial-summary',
        name: 'FinancialSummary',
        component: () => import('../views/finance/FinancialSummary.vue')
      },
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('../views/reports/Reports.vue')
      },
      {
        path: 'reports/net-balance',
        name: 'NetBalanceReport',
        component: () => import('../views/reports/NetBalanceReport.vue')
      },
      {
        path: 'ledger',
        name: 'Ledger',
        component: () => import('../views/finance/Ledger.vue')
      },
      {
        path: 'commissions',
        name: 'Commissions',
        component: () => import('../views/finance/Commissions.vue')
      },
      {
        path: 'currencies',
        name: 'Currencies',
        component: () => import('../views/finance/Currencies.vue')
      },
      {
        path: 'admin-users',
        name: 'AdminUsers',
        component: () => import('../views/admin/AdminUsers.vue')
      },
      {
        path: 'admin-roles',
        name: 'AdminRoles',
        component: () => import('../views/admin/AdminRoles.vue')
      },
      {
        path: 'active-sessions',
        name: 'ActiveSessions',
        component: () => import('../views/security/ActiveSessions.vue')
      },
      {
        path: 'ip-whitelists',
        name: 'IPWhitelists',
        component: () => import('../views/security/IPWhitelists.vue')
      },
      {
        path: 'audit-logs',
        name: 'AuditLogs',
        component: () => import('../views/security/AuditLogs.vue')
      },
      {
        path: 'callback-logs',
        name: 'CallbackLogs',
        component: () => import('../views/security/CallbackLogs.vue')
      },
      {
        path: 'alert-numbers',
        name: 'AlertNumbers',
        component: () => import('../views/security/AlertNumbers.vue')
      },
      {
        path: 'support',
        name: 'Support',
        component: () => import('../views/support/Support.vue')
      },
      {
        path: 'system-config',
        name: 'SystemConfig',
        component: () => import('../views/system/SystemConfig.vue')
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('../views/profile/Profile.vue')
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('../views/settings/Settings.vue')
      },
      {
        path: '2fa',
        name: 'TwoFactorAuth',
        component: () => import('../views/settings/TwoFactorAuth.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard'
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Always process a new callback, even when a previous account is cached.
  if (to.path === '/login' && (to.query.code || to.query.error || to.query.local === 'true' || to.query.auth === 'local')) {
    return next()
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)

  if (requiresAuth && !authStore.isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }

  if (authStore.isAuthenticated && (requiresAuth || requiresGuest)) {
    try {
      const valid = await authStore.checkValidity()
      if (!valid) {
        return next({ path: '/login', query: { error: 'access_denied' } })
      }
      if (requiresGuest) return next('/dashboard')
    } catch {
      return next({ path: '/login', query: { error: 'verification_unavailable' } })
    }
  }

  next()
})

export default router
